import React, { useState, useRef, useEffect, useContext, useMemo } from 'react';
import { MenuFoldOutlined, MenuUnfoldOutlined, DashboardOutlined, UserOutlined, SettingOutlined, FileOutlined, BarChartOutlined, EnvironmentOutlined, AppstoreOutlined, ShoppingCartOutlined, LogoutOutlined,
 MessageOutlined,
 ContactsOutlined,
 ShopOutlined} from '@ant-design/icons';
import { Avatar, Badge, Button, Menu } from 'antd';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '@api/axiosInstance';
import { ClientContext } from '@context/ClientContext';
import { useAppConfig } from '../context/AppConfigContext';

const NavbarContainer = styled.div`
  display: flex;
  width: 100%;
  min-height: calc(52px + env(safe-area-inset-top));
  background-color: #15151f;
  color: #fff;
  align-items: center;
  padding: env(safe-area-inset-top) 10px 0;
  box-sizing: border-box;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
`;

const MenuButton = styled.button`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 7px;
  color: inherit;
  cursor: pointer;
  font-size: 18px;
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const MenuContainer = styled.div`
  position: fixed;
  top: calc(52px + env(safe-area-inset-top));
  left: 0;
  width: 100%;
  background-color: #15151f;
  z-index: 1000;
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  max-height: calc(100dvh - 52px - env(safe-area-inset-top));
  overflow-y: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 16px 28px rgba(15, 23, 42, 0.28);
`;

const StyledMenu = styled(Menu)`
  background-color: transparent !important;
  color: #d0d5dd !important;
  border-inline-end: 0 !important;
  padding: 6px 8px;
  
  .ant-menu-item,
  .ant-menu-submenu-title {
    color: #d0d5dd !important;
    border-radius: 7px;
    min-height: 44px !important;
    height: auto !important;
    line-height: 44px !important;
    margin: 2px 0 !important;
  }

  .ant-menu-item:hover,
  .ant-menu-submenu-title:hover {
    background: rgba(255, 255, 255, 0.06) !important;
    color: #ffffff !important;
  }

  .ant-menu-item-selected {
    background-color: #f97316 !important;
    color: #ffffff !important;
    font-weight: 600;
  }

  .ant-menu-sub {
    background: transparent !important;
  }
`;

const MessageBadge = styled(Badge)`
  .ant-badge-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 14px !important;
  }

  .ant-scroll-number-only,
  .ant-scroll-number-only-unit {
    height: 14px !important;
    line-height: 14px !important;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  margin-left: 10px;
  
  img {
    height: 32px;
    width: auto;
  }
`;

const UserInfo = styled.div`
  padding: 10px 16px;
  color: white;
  text-align: left;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: #171722;

  div {
    font-size: 12px;
    color: #cccccc;
    line-height: 1.2;
    margin-bottom: 2px;
  }
  
  div:last-child {
    margin-bottom: 0;
  }
`;

const TopbarSpacer = styled.div`
  flex: 1;
`;

const UserPanel = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  min-width: 0;
`;

const UserMeta = styled.div`
  min-width: 0;
  flex: 1;

  .name {
    color: #ffffff;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .detail {
    color: #98a2b3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const LogoutButton = styled.button`
  background-color: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  text-align: left;
  width: 100%;
  display: flex;
  align-items: center;
  
  &:hover {
    color: white;
  }
`;

const Navbar = (props) => {
  const [isOpenState, setIsOpenState] = useState(false);
  const { appConfig } = useAppConfig();
  const { logout, user } = useAuth();
  const isAdmin = user?.is_admin === true || user?.role === 'admin';
  const { client, setClient } = useContext(ClientContext);
  const [ clientLocalStorage, setClientLocalStorage ] = useState(localStorage.getItem("selectedClient") ? JSON.parse(localStorage.getItem("selectedClient")) : undefined);

  useEffect(() => {
    if (clientLocalStorage) {
      setClient(JSON.parse(localStorage.getItem("selectedClient")));
    } 
  }, [clientLocalStorage, setClient]);
  
  const navbarRef = useRef(null);
  const menuContainerRef = useRef(null);
   const location = useLocation();
    const [ unreadMessagesCount, setUnreadMessagesCount ] = useState(0);
  const selectedKeys = useMemo(() => {
    const path = location.pathname;
    if (path.startsWith('/messages')) return ['messages'];
    if (path.startsWith('/clients')) return ['2'];
    if (path.startsWith('/expositions')) return ['3'];
    if (path.startsWith('/locations')) return ['4'];
    if (path.startsWith('/expositors')) return ['5'];
    if (path.startsWith('/orders')) return ['6'];
    if (path.startsWith('/trade')) return ['trade'];
    if (path.startsWith('/products')) return ['7'];
    if (path.startsWith('/reports')) return ['8'];
    if (path.startsWith('/visits/planner')) return ['visits-planner'];
    if (path.startsWith('/visits')) return ['visits'];
    if (path.startsWith('/settings/users')) return ['settings-users'];
    if (path.startsWith('/settings/document-types')) return ['settings-document-types'];
    if (path.startsWith('/settings/document-statuses')) return ['settings-document-statuses'];
    if (path.startsWith('/settings/change-newsletter')) return ['settings-change-newsletter'];
    if (path.startsWith('/settings/configuration')) return ['settings-configuration'];
    return ['1'];
  }, [location.pathname]);
  
    useEffect(() => {
      const fetchUnreadMessages = async () => {
        try {
          const response = await axiosInstance.get('/messages/get/unreadMessagesList');
          let sum = 0;
          response.data.forEach((item) => {
            sum++;
          })
          setUnreadMessagesCount(sum);
        } catch (error) {
          console.error('Błąd podczas pobierania nieprzeczytanych wiadomości:', error);
        }
      };
      fetchUnreadMessages();
      window.addEventListener('quickfox:messages-updated', fetchUnreadMessages);

      return () => {
        window.removeEventListener('quickfox:messages-updated', fetchUnreadMessages);
      };
    }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpenState && 
        navbarRef.current && 
        menuContainerRef.current && 
        !navbarRef.current.contains(event.target) && 
        !menuContainerRef.current.contains(event.target)
      ) {
        setIsOpenState(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpenState]);

  const toggleMenu = () => {
    setIsOpenState(!isOpenState);
  };

  const handleLogout = () => {
    logout();
    setIsOpenState(false);
  };

  const tradeMenuItem = appConfig.features?.tradeDocuments
    ? {
        key: 'trade',
        icon: <ShopOutlined />,
        label: <Link to="/trade" onClick={() => setIsOpenState(false)}>Handel</Link>,
      }
    : null;

  const baseItems = !client ? [
    { key: '1', icon: <DashboardOutlined />, label: <Link to="/" onClick={() => setIsOpenState(false)}>Dashboard</Link> },
    {
      label: <Link to='/messages' onClick={() => setIsOpenState(false)}>Wiadomości</Link>,
      key: 'messages',
      icon: (
        <MessageBadge count={unreadMessagesCount} size="small" offset={[4, -2]}>
          <MessageOutlined style={{ color: 'inherit' }} />
        </MessageBadge>
      ),
    },
    { key: '2', icon: <UserOutlined />, label: <Link to="/clients" onClick={() => setIsOpenState(false)}>Klienci</Link> },
    ...(tradeMenuItem ? [tradeMenuItem] : []),
    {
      key: 'sub1',
      icon: <AppstoreOutlined />,
      label: 'Ekspozycja',
      children: [
        { key: '3', icon: <FileOutlined />, label: <Link to="/expositions" onClick={() => setIsOpenState(false)}>Ekspozycja</Link> },
        { key: '4', icon: <EnvironmentOutlined />, label: <Link to="/locations" onClick={() => setIsOpenState(false)}>Lokalizacje</Link> },
        { key: '5', icon: <FileOutlined />, label: <Link to="/expositors" onClick={() => setIsOpenState(false)}>Ekspozytory</Link> },
        { key: '6', icon: <ShoppingCartOutlined />, label: <Link to="/orders" onClick={() => setIsOpenState(false)}>Zlecenia</Link> },
      ],
    },
    { key: '7', icon: <FileOutlined />, label: <Link to="/products" onClick={() => setIsOpenState(false)}>Produkty</Link> },
    { key: '8', icon: <BarChartOutlined />, label: <Link to="/reports" onClick={() => setIsOpenState(false)}>Raporty</Link> },
    {
      key: 'visits',
      icon: <ContactsOutlined />,
      label: <Link to="/visits" onClick={() => setIsOpenState(false)} style={{ color: 'inherit' }}>Wizyty</Link>,
      children: [
        { key: 'visits-planner', label: <Link to="/visits/planner" onClick={() => setIsOpenState(false)}>Planer wizyt</Link> },
      ],
    },
    { key: '13', icon: <LogoutOutlined />, label: <LogoutButton onClick={handleLogout}>Wyloguj</LogoutButton> },
  ] : [
      { label: <Link to="/" onClick={() => setIsOpenState(false)}>Dashboard</Link>, key: '1', icon: <DashboardOutlined /> },
      ...(tradeMenuItem ? [tradeMenuItem] : []),
      /* { label: <Link to='/messages'>Wiadomości: {unreadMessagesCount}</Link>, key: 'messages', icon: <MessageOutlined/>}, */
      /* { label: <Link to="/clients">Klienci</Link>, key: '2', icon: <UserOutlined /> }, */
      { label: <Link to="/expositions" onClick={() => setIsOpenState(false)}>Ekspozycja</Link>, key: '3', icon: <FileOutlined /> },
      { label: <Link to="/orders" onClick={() => setIsOpenState(false)}>Zlecenia</Link>, key: '6', icon: <ShoppingCartOutlined /> },
      { label: <Link to="/expositors" onClick={() => setIsOpenState(false)}>Ekspozytory</Link>, key: '5', icon: <FileOutlined /> },
      { label: <Link to="/products" onClick={() => setIsOpenState(false)}>Produkty</Link>, key: '7', icon: <FileOutlined /> },
      /* { label: <Link to="/reports">Raporty</Link>, key: '8', icon: <BarChartOutlined /> }, */
      { 
        label: <LogoutButton onClick={handleLogout}><LogoutOutlined style={{marginRight: '10px'}} />Wyloguj</LogoutButton>,
        key: '13'
      },
    ];
  
  const items = isAdmin 
    ? [
        ...baseItems.slice(0, -1),
        {
          key: 'sub2',
          icon: <SettingOutlined />,
          label: 'Ustawienia',
          children: [
            { key: 'settings-users', label: <Link to="/settings/users" onClick={() => setIsOpenState(false)}>Użytkownicy i uprawnienia</Link> },
            { key: 'settings-document-types', label: <Link to="/settings/document-types" onClick={() => setIsOpenState(false)}>Typy dokumentów</Link> },
            { key: 'settings-document-statuses', label: <Link to="/settings/document-statuses" onClick={() => setIsOpenState(false)}>Statusy dokumentów</Link> },
            { key: 'settings-change-newsletter', label: <Link to="/settings/change-newsletter" onClick={() => setIsOpenState(false)}>Powiadomienia o zmianach</Link> },
            { key: 'settings-configuration', label: <Link to="/settings/configuration" onClick={() => setIsOpenState(false)}>Konfiguracja</Link> },
          ],
        },
        baseItems[baseItems.length - 1]
      ]
    : baseItems;

  return (
    <div {...props}>
      <NavbarContainer ref={navbarRef}>
        <MenuButton
          type="button"
          onClick={toggleMenu}
          aria-label={isOpenState ? 'Zamknij menu główne' : 'Otwórz menu główne'}
          aria-expanded={isOpenState}
          aria-controls="quickfox-mobile-menu"
        >
          {isOpenState ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </MenuButton>
        <Logo>
          <img
            src={appConfig.logoUrl || '/images/logo_quickfox_horizontal.png'}
            alt={appConfig.appName || 'QuickFox'}
          />
        </Logo>
        <TopbarSpacer />
        {user && !client && (
          <Avatar size={28} style={{ background: '#f97316', flex: '0 0 auto' }}>
            {`${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() || 'Q'}
          </Avatar>
        )}
      </NavbarContainer>
      
      <MenuContainer id="quickfox-mobile-menu" $isOpen={isOpenState} ref={menuContainerRef}>
        <StyledMenu
          selectedKeys={selectedKeys}
          defaultOpenKeys={['trade', 'sub1', 'visits', 'sub2']}
          mode="inline"
          theme="dark"
          items={items}
        />
        
        {user && (
          <UserInfo>
            {(client) ? (
              <>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <span>
                    Wybrany klient: {client?.company_name}
                  </span>
                  <Button size="small" onClick={() => {
                    setClient(undefined);
                    setClientLocalStorage(undefined);
                    localStorage.removeItem("selectedClient");
                    setIsOpenState(false);
                  }} style={{ marginTop: 8, opacity: 0.9, width: '100%' }}>Wyczyść</Button>
                </div>
              </>
            ) : (
            <UserPanel>
              <Avatar size={30} style={{ background: '#f97316', flex: '0 0 auto' }}>
                {`${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() || 'Q'}
              </Avatar>
              <UserMeta>
                <div className="name">{user.first_name} {user.last_name}</div>
                <div className="detail">{user.email}</div>
                <div className="detail">{user.role}</div>
              </UserMeta>
            </UserPanel>
          )}
          </UserInfo>
        )}
      </MenuContainer>
    </div>
  );
};

export default Navbar;
